import React from 'react';

import {
  Badge as BadgeIcon,
  Mail as MailIcon,
  People as PeopleIcon,
  PersonSearch as PersonSearchIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Grid, Paper } from '@mui/material';

import {
  type EmployeeDataEmploymentRecordInfoFragment,
  type EmployeeDataInfoFragment,
  EmployeeSearchInputKindEnum,
  type EmployeeSearchMatchInfoFragment,
  useDeleteEmployeeAccountDataMutation,
  useReactivateEmployeeAccountMutation,
} from 'src/api';
import { type TabItem, Tabs } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import type { EmployeeInviteLinkArgs, ResendInviteScope } from 'src/hooks/employee';
import { useModalV2 } from 'src/hooks/shared';
import { EntityActionConfirmationModal } from 'src/modals/common';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';
import type { SxProp } from 'src/types/mui';

import { EmailSearchForm } from './EmailSearchForm';
import { EmployeeDataDetailView } from './EmployeeDataDetailView';
import { EmployeeDataResultsSection } from './EmployeeDataResultsSection';
import { EmployeeIdSearchForm } from './EmployeeIdSearchForm';
import { EmployeeSearchMatchList } from './EmployeeSearchMatchList';
import { IdentitySearchForm } from './IdentitySearchForm';
import { PhoneSearchForm } from './PhoneSearchForm';

type SelectedRecord = {
  entry: EmployeeDataInfoFragment;
  record: EmployeeDataEmploymentRecordInfoFragment;
};

export type EmployeeDataSearchScreenProps = {
  allowEmailSearch?: boolean;
  /** Hide the "Employer: Live / Not Live" indicator (employer-side roles). */
  hideEmployerLiveIndicator?: boolean;
  /** Admin-only summary controls (internal notes section, user block). */
  enableAdminControls?: boolean;
  /**
   * Latest search criteria. When `null` the user hasn't run a search yet and
   * the results panel shows its empty state.
   */
  criteria: EmployeeSearchCriteria | null;
  onRunSearch: (criteria: EmployeeSearchCriteria) => void;
  /**
   * The screen was opened on one specific employee (e.g. from a data dispute).
   * Searching is hidden — the record loads straight away instead.
   */
  directMode?: boolean;
  /** Drops the search from the URL; the way out of direct mode. */
  onClearSearch?: () => void;
  /** Whether the employee search itself is in flight. */
  searchLoading?: boolean;
  /** Raw search matches; more than one renders the picker beside the results. */
  matches?: EmployeeSearchMatchInfoFragment[];
  selectedSsnEncrypted?: string;
  onSelectMatch?: (ssnEncrypted: string) => void;
  /** Full employment data for the currently selected match. */
  employeeData?: EmployeeDataInfoFragment;
  employeeDataLoading?: boolean;
  /** Shown in place of results when the search can't run (e.g. entity not on MDM). */
  errorMessage?: string;
  /** Invite actions surfaced in the summary card actions menu. */
  inviteLinkArgs?: EmployeeInviteLinkArgs | null;
  employeeSsnEncrypted?: string;
  inviteEmailOptionsScope?: 'admin' | 'employerScope';
  sendInviteScope?: ResendInviteScope;
  onResendInvite?: (employeeId: string, inviteLinkArgs?: EmployeeInviteLinkArgs | null) => void;
  refetchEmployeeData?: () => void;
};

export const EmployeeDataSearchScreen: React.FC<EmployeeDataSearchScreenProps> = ({
  allowEmailSearch = false,
  hideEmployerLiveIndicator = false,
  enableAdminControls = false,
  criteria,
  onRunSearch,
  directMode = false,
  onClearSearch,
  searchLoading = false,
  matches,
  selectedSsnEncrypted,
  onSelectMatch,
  employeeData,
  employeeDataLoading = false,
  errorMessage,
  inviteLinkArgs,
  employeeSsnEncrypted,
  inviteEmailOptionsScope,
  sendInviteScope,
  onResendInvite,
  refetchEmployeeData,
}) => {
  const { isMobile } = useScreenInfo();

  const { createApolloContext } = useNotification();
  const { openModal } = useModalV2();

  const [selected, setSelected] = React.useState<SelectedRecord | null>(null);

  const [deleteEmployeeAccountData] = useDeleteEmployeeAccountDataMutation({
    context: createApolloContext({
      successMessage: 'Employee data has been successfully deleted',
    }),
  });

  const [reactivateEmployeeAccount] = useReactivateEmployeeAccountMutation({
    context: createApolloContext({
      successMessage: 'Data delete has been successfully canceled',
    }),
  });

  const handleOpenRecord = React.useCallback(
    (entry: EmployeeDataInfoFragment, record: EmployeeDataEmploymentRecordInfoFragment) => {
      setSelected({ entry, record });
    },
    [],
  );

  // `errorMessage` is the sticky error of the last query; hide it when the user
  // switches tabs (no new query runs on switch) and un-hide when they search again.
  const [errorDismissed, setErrorDismissed] = React.useState(false);
  const handleRunSearch = React.useCallback(
    (criteria: EmployeeSearchCriteria) => {
      setErrorDismissed(false);
      onRunSearch(criteria);
    },
    [onRunSearch],
  );

  const handleEmployeeDataDelete = React.useCallback(
    (employeeId: string) => {
      openModal(EntityActionConfirmationModal, {
        id: ModalId.EntityActionConfirmation,
        onSubmit: async () => {
          await deleteEmployeeAccountData({ variables: { employeeId } });
          refetchEmployeeData?.();
        },
        action: 'delete',
        entity: 'data',
        actionButtonColor: 'error',
      });
    },
    [openModal, deleteEmployeeAccountData, refetchEmployeeData],
  );

  const handleEmployeeDataDeleteCancel = React.useCallback(
    (employeeId: string) => {
      openModal(EntityActionConfirmationModal, {
        id: ModalId.EntityActionConfirmation,
        onSubmit: async () => {
          await reactivateEmployeeAccount({ variables: { employeeId } });
          refetchEmployeeData?.();
        },
        action: 'cancel',
        entity: 'the data delete',
        actionButtonColor: 'error',
      });
    },
    [openModal, reactivateEmployeeAccount, refetchEmployeeData],
  );

  // Disable the form for the whole search: both the `SearchEmployees*` query and the
  // follow-up `GetEmployeeDataOnEmployeeSearch*` fetch, so it doesn't flicker back on
  // between the two.
  const isSearching = searchLoading || employeeDataLoading;

  // The search restored from the URL, captured once: it prefills the form it
  // came from and opens that form's tab, and must not fight later edits.
  const [initialCriteria] = React.useState(criteria);

  const tabs = React.useMemo<TabItem<EmployeeSearchInputKindEnum>[]>(() => {
    const identityTab: TabItem<EmployeeSearchInputKindEnum> = {
      key: EmployeeSearchInputKindEnum.PersonalInfo,
      label: 'Search by Identity',
      icon: <PersonSearchIcon fontSize="small" />,
      component: (
        <IdentitySearchForm
          onSubmit={handleRunSearch}
          loading={isSearching}
          initialCriteria={initialCriteria}
        />
      ),
    };
    const employeeIdTab: TabItem<EmployeeSearchInputKindEnum> = {
      key: EmployeeSearchInputKindEnum.EmployeeId,
      label: 'Search by Employee ID',
      icon: <BadgeIcon fontSize="small" />,
      component: (
        <EmployeeIdSearchForm
          onSubmit={handleRunSearch}
          loading={isSearching}
          initialCriteria={initialCriteria}
        />
      ),
    };
    const phoneTab: TabItem<EmployeeSearchInputKindEnum> = {
      key: EmployeeSearchInputKindEnum.Phone,
      label: 'Search by Phone',
      icon: <PhoneIcon fontSize="small" />,
      component: (
        <PhoneSearchForm
          onSubmit={handleRunSearch}
          loading={isSearching}
          initialCriteria={initialCriteria}
        />
      ),
    };
    const emailTab: TabItem<EmployeeSearchInputKindEnum> = {
      key: EmployeeSearchInputKindEnum.Email,
      label: 'Search by Email',
      icon: <MailIcon fontSize="small" />,
      component: (
        <EmailSearchForm
          onSubmit={handleRunSearch}
          loading={isSearching}
          initialCriteria={initialCriteria}
        />
      ),
    };
    return [identityTab, employeeIdTab, phoneTab, ...(allowEmailSearch ? [emailTab] : [])];
  }, [allowEmailSearch, isSearching, handleRunSearch, initialCriteria]);

  // Guarded against a kind this page doesn't offer (e.g. `email` without `allowEmailSearch`).
  const initialTabKey = tabs.some(tab => tab.key === initialCriteria?.kind)
    ? initialCriteria?.kind
    : undefined;

  const resultsSection = (
    <EmployeeDataResultsSection
      loading={isSearching}
      result={employeeData}
      searched={criteria !== null}
      directMode={directMode}
      hideEmployerLiveIndicator={hideEmployerLiveIndicator}
      enableAdminControls={enableAdminControls}
      inviteLinkArgs={inviteLinkArgs}
      employeeSsnEncrypted={employeeSsnEncrypted ?? selectedSsnEncrypted}
      inviteEmailOptionsScope={inviteEmailOptionsScope}
      sendInviteScope={sendInviteScope}
      onResendInvite={onResendInvite}
      onInviteSent={refetchEmployeeData}
      onDataDelete={handleEmployeeDataDelete}
      onDataDeleteCancel={handleEmployeeDataDeleteCancel}
      onOpenRecord={handleOpenRecord}
    />
  );

  return (
    <React.Fragment>
      {/* Keep the search subtree mounted (not unmounted) while the detail view is
          open so the active tab and entered form values survive the round-trip. */}
      <React.Activity mode={selected ? 'hidden' : 'visible'}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Paper sx={panelStyles}>
            {directMode ? (
              <Alert
                severity="info"
                action={
                  onClearSearch && (
                    <Button color="inherit" size="small" onClick={onClearSearch}>
                      Search for an employee
                    </Button>
                  )
                }
              >
                You're viewing a single employee, so search is unavailable here.
              </Alert>
            ) : (
              <Tabs
                tabs={tabs}
                keepMounted
                initialTabKey={initialTabKey}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                scrollButtons={isMobile}
                allowScrollButtonsMobile={isMobile}
                additionalTabStyles={{ px: 1 }}
                onChange={() => setErrorDismissed(true)}
              />
            )}
          </Paper>

          <Paper sx={panelStyles}>
            {errorMessage && !searchLoading && !errorDismissed ? (
              <Alert severity="warning">{errorMessage}</Alert>
            ) : !directMode && matches && matches.length > 1 ? (
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12 }}>
                  {' '}
                  <Alert severity="warning" icon={<PeopleIcon />}>
                    <AlertTitle> Your search returned {matches.length} matches</AlertTitle>
                    Refine your search to narrow the results
                  </Alert>
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                  <EmployeeSearchMatchList
                    matches={matches}
                    selectedSsnEncrypted={selectedSsnEncrypted}
                    onSelect={onSelectMatch}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 8, xl: 9 }}>{resultsSection}</Grid>
              </Grid>
            ) : (
              resultsSection
            )}
          </Paper>
        </Box>
      </React.Activity>

      {selected && selectedSsnEncrypted && (
        <EmployeeDataDetailView
          entry={selected.entry}
          record={selected.record}
          employeeSsnEncrypted={selectedSsnEncrypted}
          onBack={() => setSelected(null)}
        />
      )}
    </React.Fragment>
  );
};

const panelStyles: SxProp = {
  padding: 3,
  boxShadow: '-5px 5px 30px 0 rgba(171, 171, 171, 0.25)',
};
